import {ModalityCodeEnum} from 'domain/usecases/private/refund/request/post-create-request';
import {ChipColorEnum} from 'presentation/components/TagChip/types';
import {RefundDocumentRow, RefundDocumentStatusEnum, StatusBadgeMeta} from './types';

const buildPendingRow = (
	id: string,
	documento: string,
	obrigatorio: boolean,
): RefundDocumentRow => ({
	id,
	documento,
	obrigatorio,
	status: RefundDocumentStatusEnum.PENDENTE,
	dataEnvio: null,
	anexo: null,
});

const OUTROS_ROW = buildPendingRow('outros', 'Outros*', false);

const DOCUMENTS_FOR_APPOINTMENT: RefundDocumentRow[] = [
	buildPendingRow('nota-fiscal-ou-recibo', 'Nota fiscal ou recibo', true),
	OUTROS_ROW,
];

const DOCUMENTS_FOR_EXAMS: RefundDocumentRow[] = [
	buildPendingRow('discriminacao-exames', 'Discriminação dos exames', true),
	buildPendingRow('pedido-medico', 'Pedido médico', true),
	OUTROS_ROW,
];

const DOCUMENTS_FOR_HOSPITAL: RefundDocumentRow[] = [
	buildPendingRow('conta-hospitalar-detalhada', 'Conta hospitalar detalhada', true),
	buildPendingRow('honorario-medico', 'Honorário médico', true),
	buildPendingRow('relatorio-medico-detalhado', 'Relatório médico detalhado', true),
	OUTROS_ROW,
];

const DOCUMENT_ROWS_BY_MODALITY: Record<ModalityCodeEnum, RefundDocumentRow[]> = {
	[ModalityCodeEnum.MEDICAL_APPOINTMENT]: DOCUMENTS_FOR_APPOINTMENT,
	[ModalityCodeEnum.SPECIAL_EXAMS]: DOCUMENTS_FOR_EXAMS,
	[ModalityCodeEnum.SIMPLE_EXAMS]: DOCUMENTS_FOR_EXAMS,
	[ModalityCodeEnum.HOSPITALIZATIONS]: DOCUMENTS_FOR_HOSPITAL,
	[ModalityCodeEnum.EMERGENCY]: DOCUMENTS_FOR_HOSPITAL,
	[ModalityCodeEnum.THERAPIES]: DOCUMENTS_FOR_APPOINTMENT,
};

function getDocumentRowsByModality(modalityCode?: ModalityCodeEnum): RefundDocumentRow[] {
	if (!modalityCode) return DOCUMENTS_FOR_APPOINTMENT;
	return DOCUMENT_ROWS_BY_MODALITY[modalityCode] ?? DOCUMENTS_FOR_APPOINTMENT;
}

const STATUS_BADGE_META: Record<RefundDocumentStatusEnum, StatusBadgeMeta> = {
	[RefundDocumentStatusEnum.APROVADO]: {label: 'Aprovado', color: ChipColorEnum.SUCCESS},
	[RefundDocumentStatusEnum.REJEITADO]: {label: 'Reprovado', color: ChipColorEnum.ERROR},
	[RefundDocumentStatusEnum.EM_ANALISE]: {label: 'Em análise', color: ChipColorEnum.WARNING},
	[RefundDocumentStatusEnum.PENDENTE]: {label: 'Pendente', color: ChipColorEnum.DEFAULT},
};

const TABLE_COLUMNS = {
	status: 'Status',
	documento: 'Documento',
	obrigatorio: 'Obrigatório',
	dataEnvio: 'Data de envio',
	anexo: 'Anexo',
};

const ACTIONS = {
	sendDocument: 'Enviar documento',
	replaceDocument: 'Substituir documento',
	viewDocument: 'Ver documento',
	proceedForConference: 'Prosseguir para revisão',
};

const TABLE_FOOTER_NOTE = '*Anexe quaisquer outros documentos que achar pertinente.';

const LOAD_ERROR_STATE = {
	title: 'Não foi possível carregar os documentos necessários',
	description: 'Por favor, tente novamente em alguns instantes.',
};

const REPLACE_DOCUMENT_CONFIRM = {
	headerTitle: 'Atenção',
	title: 'Tem certeza que deseja substituir o documento enviado?',
	description: 'Essa ação não poderá ser desfeita',
	confirm: 'Prosseguir',
	cancel: 'Cancelar',
};

const MISSING_REQUIRED_DOCS_CONFIRM = {
	headerTitle: 'Atenção',
	title: 'Você ainda não enviou todos os documentos obrigatórios, deseja realmente prosseguir?',
	description:
		'Avançar sem enviar os documentos obrigatórios poderá invalidar a sua solicitação após análise do nosso time por falta de documentos necessários.',
	confirm: 'Prosseguir',
	cancel: 'Cancelar',
};

const FIRST_ACTION_TARGET_ID = 'send-documents-v2-first-action';

const TUTORIAL = {
	title: 'Ficou mais fácil anexar',
	description:
		'Para agilizar seu pedido, já separamos os documentos necessários para esta modalidade. Basta anexá-los abaixo.',
	button: 'Entendi',
	storageKey: 'sendDocumentsV2FirstAccessTutorial',
};

const REVIEW_ATTACHMENTS = {
	sectionTitle: 'Anexos',
	viewDocument: 'Ver documento',
	replaceDocument: 'Substituir documento',
};

const INVALID_DOC_CONFIRM = {
	headerTitle: 'Atenção',
	title: 'Documento não aceito',
	descriptionBefore: 'O arquivo ',
	descriptionAfter:
		' não atende os requisitos para o reembolso. Por favor, substitua-o por um documento válido clicando em Substituir documento.',
	confirm: 'Entendi',
};

const UPLOAD_TERMS = {
	headerTitle: 'Envio de documentos',
	subtitle: 'Termo de aceite para Upload de Arquivos',
	paragraphs: [
		'Prezado(a) beneficiário: O GNDI disponibiliza para você o novo Reembolso Digital. O novo Reembolso Digital vem para agilizar a análise e pagamento do seu reembolso, pelas solicitações pelo Portal ou Aplicativo GNDI easy, desde que esteja com a digitalização dos documentos obrigatórios e específicos para cada tipo de procedimento realizado.',
		'Ao utilizar essa opção, os documentos físicos e notas fiscais originais ficam sob a responsabilidade exclusiva do beneficiário e não do Grupo NotreDame Intermédica, devendo o beneficiário mantê-los em seu poder, por no mínimo 5 (cinco) anos, uma vez que o GNDI poderá solicitar ao beneficiário a entrega do documento original, o que deverá ser realizado em até 5 (cinco) dias úteis, sob a pena do processo de reembolso não ser analisado ou, caso já tenha sido concluído e pago, ser o valor devolvido ao GNDI. As demais opções de reembolso permanecem inalteradas, e nestes casos, prevalecendo as regras atuais.',
		'Importante destacar que a divisão de recibos ou nota fiscal para eventos não realizados é uma prática ilegal e pode acarretar sanções e penalidades, e por consequência a exclusão do plano de saúde.',
	],
	checkboxLabel: 'Li e concordo',
	confirm: 'Continuar',
	storageKey: 'sendDocumentsV2UploadTermsAccepted',
};

const SEND_DOCUMENT_DRAWER = {
	alert: {
		title: 'Atenção',
		description:
			'Você poderá enviar apenas um arquivo aqui, caso necessário utilize a sessão Outros para enviar os demais arquivos necessários.',
	},
	upload: {
		title: 'Adicionar documento',
		description:
			'Tipos de arquivo aceitos: DOC, DOCX, XLS, XLXS, PDF, JPEG, JPG, PNG com no máximo 10MB.',
	},
	uploadingLabel: 'Carregando',
	uploadError: {
		title: 'Não foi possível carregar o arquivo',
		retry: 'Tente novamente',
	},
	deleteConfirm: {
		headerTitle: 'Excluir anexo',
		title: 'Deseja excluir o documento adicionado?',
		description: 'Essa ação não poderá ser desfeita',
		confirm: 'Excluir',
		cancel: 'Cancelar',
	},
	cancel: 'Cancelar',
	save: 'Salvar',
};

const ALLOWED_UPLOAD_EXTENSIONS = ['doc', 'docx', 'xls', 'xlsx', 'pdf', 'jpeg', 'jpg', 'png'];
const ACCEPT_ATTR = ALLOWED_UPLOAD_EXTENSIONS.map(ext => `.${ext}`).join(',');
const MAX_SIZE_BYTES = 10 * 1024 * 1024;

export {
	getDocumentRowsByModality,
	STATUS_BADGE_META,
	TABLE_COLUMNS,
	ACTIONS,
	TABLE_FOOTER_NOTE,
	LOAD_ERROR_STATE,
	REPLACE_DOCUMENT_CONFIRM,
	MISSING_REQUIRED_DOCS_CONFIRM,
	REVIEW_ATTACHMENTS,
	INVALID_DOC_CONFIRM,
	FIRST_ACTION_TARGET_ID,
	TUTORIAL,
	UPLOAD_TERMS,
	SEND_DOCUMENT_DRAWER,
	ALLOWED_UPLOAD_EXTENSIONS,
	ACCEPT_ATTR,
	MAX_SIZE_BYTES,
};
